import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { Area } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { toast } from "sonner";
import type { CropData } from "../../lib/utils";
import { Button } from "./Button";
import { Modal, ModalBody, ModalFooter } from "./Modal";

type Props = {
  open: boolean;
  onClose: () => void;
  imageSrc: string;
  aspect: number;
  shape: "round" | "rect";
  outputWidth: number;
  outputHeight: number;
  onCropConfirm: (result: { blob: Blob; cropData: CropData }) => void;
  initialCropData?: CropData | null;
};

/**
 * Ensures the image source is a same-origin blob URL so the canvas
 * can read pixel data without CORS issues.
 */
async function ensureLocalSrc(src: string): Promise<string> {
  if (src.startsWith("blob:") || src.startsWith("data:")) return src;
  const res = await fetch(src);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/** Produce a cropped blob from source image + percentage-based crop area. */
async function createCroppedBlob(
  src: string,
  percentCrop: Area,
  outputWidth: number,
  outputHeight: number,
): Promise<Blob> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });

  const nw = img.naturalWidth;
  const nh = img.naturalHeight;

  // Raw crop in pixels from percentage of natural dimensions
  const rawX = (percentCrop.x / 100) * nw;
  const rawY = (percentCrop.y / 100) * nh;
  const rawW = (percentCrop.width / 100) * nw;
  const rawH = (percentCrop.height / 100) * nh;

  // Clamp source rect to image bounds (safety net)
  const sx = Math.max(0, rawX);
  const sy = Math.max(0, rawY);
  const sw = Math.min(nw, rawX + rawW) - sx;
  const sh = Math.min(nh, rawY + rawH) - sy;

  // Map clamped source to destination coordinates
  const dx = ((sx - rawX) / rawW) * outputWidth;
  const dy = ((sy - rawY) / rawH) * outputHeight;
  const dw = (sw / rawW) * outputWidth;
  const dh = (sh / rawH) * outputHeight;

  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d")!;

  // Fill with dark background for any areas outside the image bounds
  ctx.fillStyle = "#0c0e14";
  ctx.fillRect(0, 0, outputWidth, outputHeight);

  if (sw > 0 && sh > 0) {
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))),
      "image/png",
    );
  });
}

const MAX_ZOOM = 3;

export function ImageCropModal({
  open,
  onClose,
  imageSrc,
  aspect,
  shape,
  outputWidth,
  outputHeight,
  onCropConfirm,
  initialCropData,
}: Props) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPercent, setCroppedAreaPercent] = useState<Area | null>(null);
  const [localSrc, setLocalSrc] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const minZoom = shape === "round" ? 0.4 : aspect > 1 ? 0.6 : 1;

  useEffect(() => {
    let revoke: string | null = null;
    void ensureLocalSrc(imageSrc).then((url) => {
      setLocalSrc(url);
      if (url !== imageSrc) revoke = url;
    });
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [imageSrc]);

  useEffect(() => {
    if (!open) return;
    if (!initialCropData) {
      setCrop({ x: 0, y: 0 });
    }
    setZoom(minZoom);
  }, [open, imageSrc, minZoom, initialCropData]);

  const onCropComplete = useCallback((percentages: Area) => {
    setCroppedAreaPercent(percentages);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPercent || !localSrc) return;
    setIsProcessing(true);
    try {
      const blob = await createCroppedBlob(localSrc, croppedAreaPercent, outputWidth, outputHeight);
      onCropConfirm({
        blob,
        cropData: {
          x: croppedAreaPercent.x,
          y: croppedAreaPercent.y,
          width: croppedAreaPercent.width,
          height: croppedAreaPercent.height,
        },
      });
      onClose();
    } catch {
      toast.error("Could not process image. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} className="!max-w-lg">
      <div className="px-6 pt-6 pb-2">
        <h2 className="text-lg font-bold text-white">Position Image</h2>
        <p className="text-sm text-surface-400 mt-0.5">
          Drag to reposition, scroll to zoom in or out
        </p>
      </div>
      <ModalBody>
        <div className="relative w-full h-72 rounded-lg overflow-hidden bg-surface-950">
          {localSrc ? (
            <Cropper
              image={localSrc}
              crop={crop}
              zoom={zoom}
              minZoom={minZoom}
              maxZoom={MAX_ZOOM}
              aspect={aspect}
              cropShape={shape}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid={false}
              objectFit={shape === "round" ? "contain" : "cover"}
              restrictPosition={shape !== "round"}
              initialCroppedAreaPercentages={
                initialCropData
                  ? {
                      x: initialCropData.x,
                      y: initialCropData.y,
                      width: initialCropData.width,
                      height: initialCropData.height,
                    }
                  : undefined
              }
              style={{
                containerStyle: { borderRadius: "0.5rem" },
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="size-6 text-surface-500 animate-spin" />
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <span className="text-xs text-surface-500 shrink-0">Zoom</span>
          <input
            type="range"
            min={minZoom}
            max={MAX_ZOOM}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="zoom-slider flex-1"
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!croppedAreaPercent || !localSrc || isProcessing}
          onClick={handleConfirm}
        >
          {isProcessing ? "Processing..." : "Apply"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
